import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Box, Chip, Paper, Typography } from '@mui/material';
import ConfusionMatrix from "./ConfusionMatrix";
import PrecisionByRule from "./PrecisionByRule";
import { Assessment } from "../../../enums/Assessment";

const assessmentLabels = [Assessment.VALID, Assessment.UNFAIR, Assessment.VOID];

const getEmptyMatrix = () => assessmentLabels.reduce((matrix, trueLabel) => {
    matrix[trueLabel] = assessmentLabels.reduce((row, predictedLabel) => {
        row[predictedLabel] = 0;
        return row;
    }, {});
    return matrix;
}, {});

const calculateAverage = (values, supports = null) => {
    if (!supports) {
        return values.reduce((sum, value) => sum + value, 0) / values.length;
    }

    const totalSupport = supports.reduce((sum, support) => sum + support, 0);
    if (totalSupport === 0) return 0;

    return values.reduce((sum, value, index) => sum + value * supports[index], 0) / totalSupport;
};

const calculateMetrics = (matrix) => {
    const total = assessmentLabels.reduce((sum, trueLabel) => (
        sum + assessmentLabels.reduce((rowSum, predictedLabel) => rowSum + matrix[trueLabel][predictedLabel], 0)
    ), 0);

    const perClass = assessmentLabels.map(label => {
        const truePositive = matrix[label][label];
        const falsePositive = assessmentLabels
            .filter(trueLabel => trueLabel !== label)
            .reduce((sum, trueLabel) => sum + matrix[trueLabel][label], 0);
        const falseNegative = assessmentLabels
            .filter(predictedLabel => predictedLabel !== label)
            .reduce((sum, predictedLabel) => sum + matrix[label][predictedLabel], 0);
        const support = assessmentLabels.reduce((sum, predictedLabel) => sum + matrix[label][predictedLabel], 0);
        const precision = truePositive + falsePositive === 0 ? 0 : truePositive / (truePositive + falsePositive);
        const recall = truePositive + falseNegative === 0 ? 0 : truePositive / (truePositive + falseNegative);
        const f1 = precision + recall === 0 ? 0 : (2 * precision * recall) / (precision + recall);

        return { label, precision, recall, f1, support };
    });

    return {
        total,
        perClass,
        macro: {
            precision: calculateAverage(perClass.map(metric => metric.precision)),
            recall: calculateAverage(perClass.map(metric => metric.recall)),
            f1: calculateAverage(perClass.map(metric => metric.f1))
        },
        weighted: {
            precision: calculateAverage(perClass.map(metric => metric.precision), perClass.map(metric => metric.support)),
            recall: calculateAverage(perClass.map(metric => metric.recall), perClass.map(metric => metric.support)),
            f1: calculateAverage(perClass.map(metric => metric.f1), perClass.map(metric => metric.support))
        }
    };
};

const preparePerformanceData = (reviews) => {
    const llmMap = new Map();
    const reviewsByClause = reviews.reduce((acc, review) => {
        if (!acc[review.clause]) {
            acc[review.clause] = [];
        }
        acc[review.clause].push(review);

        if (review.author?.kind === 'LLM') {
            const llmId = review.author._id;
            const existingLlm = llmMap.get(llmId);
            if (!existingLlm || new Date(review.author.createdAt) > new Date(existingLlm.createdAt)) {
                llmMap.set(llmId, {
                    id: llmId,
                    name: review.author.name,
                    createdAt: review.author.createdAt
                });
            }
        }

        return acc;
    }, {});

    const llms = Array.from(llmMap.values())
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const byLlm = llms.reduce((acc, llm) => {
        const matrix = getEmptyMatrix();
        const filteredReviews = [];

        Object.values(reviewsByClause).forEach(clauseReviews => {
            const llmReview = clauseReviews
                .filter(review => review.author?.kind === 'LLM' && review.author?._id === llm.id)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
            const humanReview = clauseReviews
                .filter(review => review.author?.kind !== 'LLM')
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

            if (llmReview) {
                filteredReviews.push(llmReview);
            }
            if (humanReview) {
                filteredReviews.push(humanReview);
            }

            if (!llmReview) return;

            const trueAssessment = humanReview ? humanReview.assessment : llmReview.assessment;
            if (!assessmentLabels.includes(llmReview.assessment) || !assessmentLabels.includes(trueAssessment)) return;

            matrix[trueAssessment][llmReview.assessment] += 1;
        });

        acc[llm.id] = {
            matrix,
            metrics: calculateMetrics(matrix),
            reviews: filteredReviews
        };

        return acc;
    }, {});

    return { llms, byLlm };
};

const formatPercent = (value) => `${(value * 100).toFixed(1)}%`;

const AiPerformanceTab = () => {
    const [reviews, setReviews] = useState(null);
    const [rules, setRules] = useState(null);
    const [selectedLlmId, setSelectedLlmId] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get('/analytics/ai-performance');
                setReviews(response.data.reviews || []);
                setRules(response.data.rules || []);
            } catch (err) {
                console.error("Error fetching data:", err);
            }
        };

        fetchData();
    }, []);

    const performanceData = useMemo(() => {
        if (!reviews) return { llms: [], byLlm: {} };
        return preparePerformanceData(reviews);
    }, [reviews]);

    useEffect(() => {
        if (performanceData.llms.length === 0) {
            setSelectedLlmId(null);
            return;
        }

        if (!selectedLlmId || !performanceData.byLlm[selectedLlmId]) {
            setSelectedLlmId(performanceData.llms[0].id);
        }
    }, [performanceData, selectedLlmId]);

    const selectedData = selectedLlmId ? performanceData.byLlm[selectedLlmId] : null;

    return (
        <Box>
            {reviews && performanceData.llms.length === 0 && (
                <Typography color="text.secondary">
                    Keine KI-Bewertungen für Verträge ab abgeschlossener Anwaltsbewertung vorhanden.
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {performanceData.llms.map(llm => (
                    <Chip
                        key={llm.id}
                        label={llm.name}
                        color={selectedLlmId === llm.id ? 'primary' : 'default'}
                        variant={selectedLlmId === llm.id ? 'filled' : 'outlined'}
                        onClick={() => setSelectedLlmId(llm.id)}
                    />
                ))}
            </Box>

            <Box sx={{mt: '2em'}}>
                <ConfusionMatrix matrix={selectedData?.matrix} total={selectedData?.metrics.total}></ConfusionMatrix>
            </Box>

            {selectedData && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mt: 3 }}>
                    <Paper elevation={0} sx={{ p: 2 }}>
                        <Typography variant="h6" fontWeight="bold">Makro-Durchschnitt</Typography>
                        <Typography color="text.secondary">Precision: {formatPercent(selectedData.metrics.macro.precision)}</Typography>
                        <Typography color="text.secondary">Recall: {formatPercent(selectedData.metrics.macro.recall)}</Typography>
                        <Typography color="text.secondary">F1: {formatPercent(selectedData.metrics.macro.f1)}</Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ p: 2 }}>
                        <Typography variant="h6" fontWeight="bold">Gewichteter Durchschnitt</Typography>
                        <Typography color="text.secondary">Precision: {formatPercent(selectedData.metrics.weighted.precision)}</Typography>
                        <Typography color="text.secondary">Recall: {formatPercent(selectedData.metrics.weighted.recall)}</Typography>
                        <Typography color="text.secondary">F1: {formatPercent(selectedData.metrics.weighted.f1)}</Typography>
                    </Paper>
                </Box>
            )}

            <Box sx={{mt: '2em'}}>
                <PrecisionByRule reviews={selectedData?.reviews} rules={rules}></PrecisionByRule>
            </Box>
        </Box>
    );
};

export default AiPerformanceTab;
